import styled from 'styled-components'
import { themeGet } from 'styled-system'
import {
  ContainedButton,
  DateRange,
  EditIcon,
  OutlinedButton,
  HeaderTwo,
  SearchSelect,
  WarningIcon,
} from 'secondstep-components'
import ProgressBar from 'components/ProgressBar'

export const BubbleNavigatorWrapper = styled.div`
  display: flex;
  width: 100%;
  padding-bottom: 1rem;
  align-items: center;
  box-sizing: border-box;
`

export const DateText = styled.p`
  margin-bottom: 0.25rem;
`

export const EditButton = styled.button`
  border: none;
  cursor: pointer;
  color: ${themeGet('colors.blue')};
  transition: color 0.2s ease;
  font-size: 0.875rem;
  background: none;
  margin-bottom: 0.1875rem;
  padding: 0 0.375rem;

  &:hover {
    color: ${themeGet('colors.darkBlue')};
    transition: color 0.2s ease;
  }
`

export const EstimatedTime = styled.span`
  font-size: 1rem;
  font-family: ${themeGet('font.molde.regular')};
  font-style: italic;
  color: ${themeGet('colors.darkGray')};
`

export const Image = styled.img`
  width: 75px;
  height: 75px;
`

export const GetStartedButton = styled(ContainedButton).attrs(
  ({ dataTestId }) => ({
    'data-testid': dataTestId,
  }),
)`
  min-width: 123px;
  flex: none;
  background-color: ${themeGet('colors.elemDigital4')};
  border-color: ${themeGet('colors.elemDigital4')};
`

export const GoalCompletedText = styled.p`
  font-size: 0.875rem;
  color: ${themeGet('colors.darkGray')};
`

export const GoalContentWrapper = styled.div.attrs(
  ({
    hasUserStatsPanel,
    isSingleSite,
    selectedGoalIsCompleted,
    showHeader,
    showMonitorViewOnly,
    useColumnLayout,
  }) => ({
    alignItems:
      useColumnLayout || hasUserStatsPanel
        ? 'flex-start'
        : selectedGoalIsCompleted
        ? 'flex-start'
        : 'center',
    background:
      showMonitorViewOnly && !showHeader
        ? themeGet('colors.white')
        : themeGet('colors.paleGray'),
    flexDirection:
      hasUserStatsPanel && !useColumnLayout
        ? 'row'
        : useColumnLayout || selectedGoalIsCompleted
        ? 'column'
        : 'row',
    justifyContent: useColumnLayout ? 'flex-end' : 'flex-start',
    minHeight: '10.6875rem',
  }),
)`
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection};
  align-items: ${({ alignItems }) => alignItems};
  justify-content: ${({ justifyContent }) => justifyContent};
  position: relative;
  background-color: ${({ background }) => background};
  border-radius: 0.625rem;
  padding: 1.25rem 1.25rem;
  min-height: ${({ minHeight }) => minHeight};
  width: 100%;
  box-sizing: border-box;
`

export const GoalContentLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  flex: 1;
`

export const GoalMetricGroup = styled.div`
  display: flex;
  align-items: baseline;
`

export const GoalWarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  font-family: ${themeGet('font.molde.regular')};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${themeGet('colors.deleteRed')};
`

export const GoalNotSetWarningAlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 0.25rem;

  ${GoalWarningBanner} {
    margin-top: 0;
  }
`

export const GoalSchoolwideDisabledMessage = styled.p`
  margin: 0;
  font-family: ${themeGet('font.molde.regular')};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${themeGet('colors.darkGray')};
`

export const StyledGoalWarningIcon = styled(WarningIcon)`
  display: inline-flex;
  flex-shrink: 0;
  width: 0.75rem;
  height: 0.75rem;

  circle[fill='currentColor'] {
    fill: ${themeGet('colors.deleteRed')};
  }
`

export const GoalWarningLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    opacity: 0.85;
  }
`

export const GoalUserStatsDivider = styled.div.attrs(({ isSingleSite }) => ({
  backgroundColor: isSingleSite
    ? themeGet('colors.white')
    : themeGet('colors.paleGray'),
}))`
  width: 2px;
  align-self: stretch;
  flex-shrink: 0;
  background-color: ${({ backgroundColor }) => backgroundColor};
  margin: 0 1.25rem;
`

export const GoalUserStatsPanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0;
`

export const GoalUserStatRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  font-family: ${themeGet('font.molde.regular')};
  font-size: 1rem;
  color: ${themeGet('colors.darkGray')};

  &:first-child {
    font-size: 1.125rem;
    font-weight: 700;
  }
`

export const GoalUserStatCount = styled.span`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
`

export const GoalUserManagementLink = styled.a`
  color: ${themeGet('colors.blue')};
  font-family: ${themeGet('font.molde.regular')};
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${themeGet('colors.darkBlue')};
  }
`

export const GoalContentHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
`

export const GoalStatusBadge = styled.div.attrs(({ goalStatusText }) => ({
  background:
    goalStatusText === 'Goal Missed'
      ? 'rgba(255, 191, 63, 0.5)'
      : themeGet('colors.k5kit7'),
}))`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.875rem;
  line-height: 1.75rem;
  padding: 0.0625rem 1.25rem;
  background: ${({ background }) => background};
  border-radius: 0.5rem 1.625rem 1.25rem 4rem;

  span {
    padding-left: 4px;
  }
`

export const GoalSubTitle = styled.p`
  font-family: ${themeGet('font.molde.medium')};
  color: ${themeGet('colors.darkGray')};
  margin-top: 0.375rem;
`

export const GoalText = styled.p.attrs(({ selectedGoalIsCompleted }) => ({
  margin: selectedGoalIsCompleted ? '0 0 0.5rem' : ' 0.625rem 0 0 0.375rem',
}))`
  margin: ${({ margin }) => margin};
`

export const GoalTextLarge = styled.p`
  font-size: 2rem;
  font-family: ${themeGet('font.sharpSlab.medium')};
`

export const GoalTitleWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
`

export const PageContent = styled.div.attrs(({ dataTestId, showSummary }) => ({
  'data-testid': dataTestId,
  height: showSummary ? '100%' : 'auto',
}))`
  display: flex;
  align-items: center;
  padding: 1.25rem;
  border: 2px solid ${themeGet('colors.paleGray')};
  background: ${themeGet('colors.white')};
  border-radius: 0.625rem;
  height: ${({ height }) => height};

  #step-icon {
    min-width: 75px;
    min-height: 75px;
  }
`

export const PageNavigator = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 1.688rem 3.75rem 2.375rem;
  align-items: center;
  box-sizing: border-box;
`

export const PlaybookStepsWrapper = styled.div.attrs(
  ({ isSingleSite, showHeader, showMonitorViewOnly }) => ({
    background:
      showMonitorViewOnly && !showHeader
        ? themeGet('colors.paleGray')
        : themeGet('colors.white'),
    maxWidth: showMonitorViewOnly && showHeader ? '512px' : '100%',
    marginBottom: showMonitorViewOnly && !isSingleSite ? '1rem' : 'none',
    padding:
      showMonitorViewOnly && !isSingleSite
        ? 'none'
        : '1.1875rem 1.25rem 1.75rem',
  }),
)`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: ${({ maxWidth }) => maxWidth};
  background: ${({ background }) => background};
  border-radius: 0.5rem;
  padding: ${({ padding }) => padding};
  margin-bottom: ${({ marginBottom }) => marginBottom};

  ${themeGet('breakpoints.medium')} {
    max-width: 100%;
  }
`

export const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

export const StepNumber = styled.span`
  font-size: 0.75rem;
  letter-spacing: 1.6px;
  font-family: ${themeGet('font.molde.regular')};
  color: ${themeGet('colors.gray1100')};
`

export const StyledDateRange = styled(DateRange)`
  background: none;
  height: 26px;

  [class*='DateRangeWrapper-sc'] {
    margin-left: 0;
    margin-right: 0;
  }

  [class*='OverallBar-sc'] {
    margin-top: 12px;
  }

  [class*='CurrentMarker-sc'],
  [class*='ProgressBar-sc'] {
    border-color: ${themeGet('colors.elemDigital1')};
    margin-bottom: 3px;
  }

  [class*='StartDateText-sc'],
  [class*='EndDateText-sc'] {
    padding-top: 0;
    padding-bottom: 0;
  }
`

export const StyledEditIcon = styled(EditIcon)`
  width: 14px;
  height: 14px;
`

export const StyledGoalHr = styled.hr.attrs(({ showMonitorViewOnly }) => ({
  backgroundColor: showMonitorViewOnly
    ? themeGet('colors.paleGray')
    : themeGet('colors.white'),
}))`
  height: 2px;
  width: 100%;
  border: none;
  background-color: ${({ backgroundColor }) => backgroundColor};
`

export const StyledHeaderTwo = styled(HeaderTwo)``

export const StyledHr = styled.hr`
  height: 2px;
  width: 100%;
  border: none;
  background-color: ${themeGet('colors.blueGrayLight')};
`

export const StyledLinkDropdown = styled(SearchSelect)`
  svg {
    color: ${themeGet('colors.darkBlue')};

    &:last-of-type {
      width: 14px;
      height: 12px;
      margin-left: auto;
      color: ${themeGet('colors.white')};
    }
  }

  span {
    color: ${themeGet('colors.darkBlue')};
    font-family: ${themeGet('fontFamilySemibold')};
  }

  [class*='LabelWrapper'] {
    display: none;
  }

  [class*='DropdownButton'] {
    background: ${themeGet('colors.blue')};
    border: unset;
    border-radius: 15px;
    height: 31px;

    span {
      color: ${themeGet('colors.white')};
    }

    svg:first-of-type {
      color: ${themeGet('colors.white')};
      width: 25px;
      height: 17px;
      padding-right: 0.375rem;
    }

    ${themeGet('breakpoints.tablet')} {
      width: 12rem;
    }
  }

  [class*='DropdownOptions'] {
    border: none;
    border-radius: 0.3125rem;
    box-shadow: 0 0 22px rgba(0, 0, 0, 0.2);
    margin-top: 0.625rem;
    padding: 0.25rem 0;
    width: 18rem;

    button {
      padding: 0.125rem 1rem;

      svg {
        padding-left: 0.5rem;
      }
    }

    button:not(:last-child) {
      border-bottom: solid 1px ${themeGet('colors.paleGray')};
    }

    @media screen and (max-width: 1050px) {
      right: 0;
    }

    ${themeGet('breakpoints.tablet')} {
      right: unset;
    }
  }

  [class*='DropdownSpaceFiller'] {
    display: none;
  }

  ${themeGet('breakpoints.tablet')} {
    padding-left: 0;
  }
`

export const StyledProgressBar = styled(ProgressBar).attrs(
  ({ isGoalSet = true }) => ({
    isGoalSet,
  }),
)`
  width: 100%;
  padding: 0;
  margin-bottom: ${({ isGoalSet }) => (isGoalSet ? '1.625rem' : '0.5rem')};

  [class*='Background-sc'] {
    background-color: ${themeGet('colors.white')};
    border: 2px solid ${themeGet('colors.grayBlue')};
  }

  [class*='_Indicator-sc'] {
    height: 17px;
    top: -1px;
    left: -1px;
    background: linear-gradient(
      to right,
      ${themeGet('colors.elemDigital4')} 0%,
      ${themeGet('colors.elemDigital1')} 100%
    );
    border: 2px solid #3c6e90;
    box-sizing: border-box;
  }
`

export const SubTitle = styled.span`
  line-height: 1.25rem;
`

export const SummaryButton = styled(OutlinedButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border-radius: 15px;
  height: 31px;
  margin-top: 0.625rem;
  padding: 0.375rem 0.8125rem;
`

export const SummaryLink = styled.a`
  align-self: center;
  color: ${themeGet('colors.blue')};
  font-size: 1rem;
  font-family: ${themeGet('font.molde.medium')};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s ease;

  &:hover {
    color: ${themeGet('colors.darkBlue')};
    transition: color 0.2s ease;
  }
`

export const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2.5rem 0 1.25rem;
  width: 100%;
`

export const TierPill = styled.div`
  margin: 0 0 0.625rem 0.8125rem;

  span {
    border: 2px solid ${themeGet('colors.paleGray')};
    color: ${themeGet('colors.darkGray')};
    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    padding: 0.375rem 8px 0.25rem;
    border-radius: 6.25rem;
  }
`

export const Title = styled.span`
  margin-bottom: 0.125rem;
  font-size: 1rem;
  font-family: ${themeGet('font.sharpSlab.semibold')};
  color: ${themeGet('colors.darkBlue')};
`

export const TopWrapper = styled.div.attrs(({ showMonitorViewOnly }) => ({
  justifyContent: showMonitorViewOnly ? 'flex-start' : 'space-between',
}))`
  display: flex;
  justify-content: ${({ justifyContent }) => justifyContent};
  align-items: flex-end;
  width: 100%;
  margin-bottom: 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${themeGet('colors.paleGray')};
`
